import { IconAlertCircle, IconChevronRight, IconPhoto } from "@tabler/icons-react";
import type { TFunction } from "i18next";
import { MarketplaceBadge } from "@/components/shared/marketplace-badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  type AsinCheckState,
  type AsinItem,
  type AsinProduct,
  productCheckState,
} from "./asin-filter-model";

/**
 * Result rows of the ASIN filter popover (Paper "Popup · ASIN · results").
 * Lanes: 6px inset · 18px chevron slot · 8px · 16px checkbox · 8px · 36px thumbnail,
 * so variation rows (44px spacer) line their checkbox up under the parent's thumbnail.
 */

export const ASIN_NAV_ATTR = "data-asin-nav";

const ROW = "group/row flex w-full shrink-0 items-center gap-2 rounded-sm pr-2.5 pl-1.5 text-left";
const ROW_INTERACTIVE =
  "cursor-pointer hover:bg-control-highlight has-[:focus-visible]:bg-control-highlight";
const BADGE_ON_HIGHLIGHT = "group-hover/row:bg-surface group-has-[:focus-visible]/row:bg-surface";
const CHECKBOX = "size-4 rounded-[4px] [&_svg]:size-3 data-disabled:border-line";

type T = TFunction<"dashboard">;

function MetaDot() {
  return (
    <span aria-hidden className="text-xs text-placeholder">
      ·
    </span>
  );
}

function OutOfScope({ t }: { t: T }) {
  return (
    <span className="flex min-w-0 items-center gap-1 text-badge font-medium text-warning-foreground">
      <IconAlertCircle className="size-3 shrink-0" stroke={2} aria-hidden />
      <span className="truncate">{t("overview.filters.asin.notInScope")}</span>
    </span>
  );
}

function skuText(skus: readonly string[], t: T): string | null {
  const [first] = skus;
  if (!first) return null;
  return skus.length > 1
    ? t("overview.filters.asin.skuMore", { sku: first, count: skus.length - 1 })
    : t("overview.filters.asin.sku", { sku: first });
}

function Thumbnail({ imageUrl, disabled }: { imageUrl: string | null; disabled: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-xs border border-line",
        imageUrl ? "bg-surface" : "bg-subtle",
        disabled && "opacity-60",
      )}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          draggable={false}
          className="size-full object-contain"
        />
      ) : (
        <IconPhoto className="size-4 text-placeholder" stroke={1.75} />
      )}
    </span>
  );
}

function Badges({ codes, disabled }: { codes: readonly string[]; disabled: boolean }) {
  if (codes.length === 0) return null;
  const visible = codes.slice(0, 2);
  const rest = codes.length - visible.length;
  const className = cn(disabled ? "text-muted-foreground" : BADGE_ON_HIGHLIGHT);
  return (
    <span className="flex shrink-0 items-center gap-0.5">
      {visible.map((code) => (
        <MarketplaceBadge key={code} code={code} className={className} />
      ))}
      {rest > 0 ? <MarketplaceBadge code={`+${rest}`} className={className} /> : null}
    </span>
  );
}

function RowCheckbox({
  state,
  disabled,
  label,
  onToggle,
  groupKey,
}: {
  state: AsinCheckState;
  disabled: boolean;
  label: string;
  onToggle: () => void;
  groupKey?: string;
}) {
  return (
    <Checkbox
      checked={state === "checked"}
      indeterminate={state === "indeterminate"}
      disabled={disabled}
      onCheckedChange={onToggle}
      // The row toggles on click too; stop the bubble to avoid a double toggle.
      onClick={(event) => event.stopPropagation()}
      aria-label={label}
      {...{ [ASIN_NAV_ATTR]: "" }}
      data-asin-group={groupKey}
      className={CHECKBOX}
    />
  );
}

export interface AsinProductRowsProps {
  product: AsinProduct;
  excluded: ReadonlySet<string>;
  expanded: boolean;
  t: T;
  onToggleExpanded: () => void;
  onToggleProduct: () => void;
  onToggleAsin: (asin: string) => void;
}

/** One product: a standalone ASIN, or a parent row with its variations when expanded. */
export function AsinProductRows({
  product,
  excluded,
  expanded,
  t,
  onToggleExpanded,
  onToggleProduct,
  onToggleAsin,
}: AsinProductRowsProps) {
  const isGroup = product.parentAsin != null;
  const disabled = !product.inScope;
  const title = product.title ?? product.parentAsin ?? product.key;
  const single: AsinItem | undefined = isGroup ? undefined : product.items[0];
  const identifier = product.parentAsin ?? single?.asin ?? product.key;
  const secondary = isGroup
    ? t("overview.filters.asin.variationCount", { count: product.items.length })
    : skuText(single?.skus ?? [], t);

  return (
    // biome-ignore lint/a11y/useSemanticElements: a fieldset would add form chrome; this only names a family's rows for assistive tech.
    <div role="group" aria-label={title} className="flex flex-col">
      {/* biome-ignore lint/a11y/noStaticElementInteractions: the checkbox is the accessible control; the row is a wider hit area. */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard users operate the focusable checkbox itself. */}
      <div
        onClick={disabled ? (isGroup ? onToggleExpanded : undefined) : onToggleProduct}
        className={cn(ROW, "py-2", disabled ? "cursor-default" : ROW_INTERACTIVE)}
      >
        <span className="flex size-[18px] shrink-0 items-center justify-center">
          {isGroup ? (
            <button
              type="button"
              // Keyboard expands via →/← on the checkbox; a disabled family has no
              // focusable checkbox, so its chevron takes the stop instead.
              tabIndex={disabled ? undefined : -1}
              {...(disabled ? { [ASIN_NAV_ATTR]: "", "data-asin-group": product.key } : {})}
              aria-expanded={expanded}
              aria-label={t(
                expanded ? "overview.filters.asin.collapse" : "overview.filters.asin.expand",
                { name: title },
              )}
              onClick={(event) => {
                event.stopPropagation();
                onToggleExpanded();
              }}
              className="flex size-[18px] cursor-pointer items-center justify-center rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
            >
              <IconChevronRight
                className={cn(
                  "size-3.5 transition-transform duration-[var(--duration-fast)]",
                  expanded ? "rotate-90 text-ink" : "text-text-secondary",
                )}
                stroke={2}
                aria-hidden
              />
            </button>
          ) : null}
        </span>
        <RowCheckbox
          state={productCheckState(product, excluded)}
          disabled={disabled}
          label={`${title} · ${identifier}`}
          onToggle={onToggleProduct}
          groupKey={isGroup ? product.key : undefined}
        />
        <Thumbnail imageUrl={product.imageUrl} disabled={disabled} />
        <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <span
            className={cn(
              "truncate text-xs font-medium",
              disabled ? "text-muted-foreground" : "text-ink",
            )}
            title={title}
          >
            {title}
          </span>
          <span className="flex min-w-0 items-center gap-1.5">
            <span
              className={cn(
                "shrink-0 text-badge tabular-nums",
                disabled ? "text-muted-foreground" : "text-text-secondary",
              )}
            >
              {identifier}
            </span>
            {disabled ? (
              <>
                <MetaDot />
                <OutOfScope t={t} />
              </>
            ) : secondary ? (
              <>
                <MetaDot />
                <span className="truncate text-badge text-muted-foreground tabular-nums">
                  {secondary}
                </span>
              </>
            ) : null}
          </span>
        </span>
        <Badges codes={product.marketplaces} disabled={disabled} />
      </div>
      {isGroup && expanded
        ? product.items.map((item) => (
            <VariationRow
              key={item.asin}
              item={item}
              checked={item.inScope && !excluded.has(item.asin)}
              t={t}
              onToggle={() => onToggleAsin(item.asin)}
            />
          ))
        : null}
    </div>
  );
}

function VariationRow({
  item,
  checked,
  t,
  onToggle,
}: {
  item: AsinItem;
  checked: boolean;
  t: T;
  onToggle: () => void;
}) {
  const disabled = !item.inScope;
  const title = item.title ?? item.asin;
  const sku = skuText(item.skus, t);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the checkbox is the accessible control; the row is a wider hit area.
    // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard users operate the focusable checkbox itself.
    <div
      onClick={disabled ? undefined : onToggle}
      className={cn(ROW, "py-1.5", disabled ? "cursor-default" : ROW_INTERACTIVE)}
    >
      <span aria-hidden className="h-4 w-11 shrink-0" />
      <RowCheckbox
        state={checked ? "checked" : "unchecked"}
        disabled={disabled}
        label={`${title} · ${item.asin}`}
        onToggle={onToggle}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-px">
        <span
          className={cn(
            "truncate text-xs font-medium",
            disabled ? "text-muted-foreground" : "text-ink",
          )}
          title={title}
        >
          {title}
        </span>
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className={cn(
              "shrink-0 text-badge tabular-nums",
              disabled ? "text-muted-foreground" : "text-text-secondary",
            )}
          >
            {item.asin}
          </span>
          {disabled ? (
            <>
              <MetaDot />
              <OutOfScope t={t} />
            </>
          ) : sku ? (
            <>
              <MetaDot />
              <span className="truncate text-badge text-muted-foreground">{sku}</span>
            </>
          ) : null}
        </span>
      </span>
    </div>
  );
}

const SKELETON_WIDTHS = [
  ["w-[180px]", "w-[120px]"],
  ["w-[150px]", "w-[100px]"],
] as const;

/** Paper "Popup · ASIN · loading": two placeholder rows in the result lanes. */
export function AsinResultsSkeleton() {
  return (
    <div aria-hidden className="flex flex-col px-1 pb-1.5">
      {SKELETON_WIDTHS.map(([title, meta]) => (
        <div key={title} className="flex items-center gap-2.5 px-2.5 py-2">
          <Skeleton className="size-4 shrink-0 rounded-[4px] bg-subtle" />
          <Skeleton className="size-9 shrink-0 rounded-xs bg-subtle" />
          <span className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className={cn("h-2.5 max-w-full rounded-[4px] bg-subtle", title)} />
            <Skeleton className={cn("h-2 max-w-full rounded-[4px] bg-subtle", meta)} />
          </span>
          <Skeleton className="h-[18px] w-[22px] shrink-0 rounded-[4px] bg-subtle" />
        </div>
      ))}
    </div>
  );
}
